/**
 * Dispatch section (garment Increment 8, M9) — section 8 of the Style File.
 *
 * Faithful to `prototype/garment/style-file.html` #sec-dispatch: the packing
 * summary (cartons) + export shipping documents, and the confirm action that
 * drafts the export invoice and ships the style through the QC gate (with the
 * Owner override when a lot failed). Three states: plan (no dispatch yet) →
 * draft (edit docs, confirm) → shipped (read-only + invoice link).
 *
 * @see prototype/garment/style-file.html #sec-dispatch
 * @see packages/domain/src/garment/dispatch
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { PackageCheck, Ship, ShieldAlert, FileText, Pencil } from "lucide-react";
import { Button, Input, StatusPill, toast } from "@flint/ui";
import type { dispatch as dispatchContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import type { StyleStatus } from "../lib/status.js";
import { FormSelect } from "./form-select.js";

type DispatchData = dispatchContract.DispatchViewWire;
type Route = "export_lut" | "export_with_igst" | "taxable_igst" | "taxable_cgst_sgst";

/** Label the linked invoice by what the CA will see it as — tax invoice vs export invoice. */
function invoiceLabel(t: string | null | undefined): string {
  if (t === "export_lut" || t === "export_with_igst") return "Export invoice";
  return "Tax invoice";
}

interface DispatchSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
}

export function DispatchSection({ styleId, styleStatus }: DispatchSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.dispatch.getForStyle.useQuery({ styleId });

  if (styleStatus !== "in_production" && styleStatus !== "shipped" && styleStatus !== "closed") {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">Dispatch opens once the style is in production</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          Build the packing list, record the shipping bill / BL, then confirm to auto-draft the tax invoice and ship.
        </p>
      </div>
    );
  }

  if (query.isLoading) return <p className="text-sm text-muted">Loading dispatch…</p>;
  const data = query.data;
  if (!data) return <p className="text-sm text-muted">No dispatch data.</p>;

  const invalidate = async () => {
    await Promise.all([
      utils.dispatch.getForStyle.invalidate({ styleId }),
      utils.styles.get.invalidate({ id: styleId }),
      // Confirm ships the style (→ shipped) → refresh the sibling sections that gate on status.
      utils.qc.getForStyle.invalidate({ styleId }),
      utils.production.getForStyle.invalidate({ styleId }),
      utils.auditLog.list.invalidate(),
    ]);
  };

  return (
    <div className="flex flex-col gap-4">
      {data.qcBlocked ? (
        <div className="flex items-center gap-2 rounded-sm border border-bad-100 border-l-[3px] border-l-bad bg-bad-50 px-3 py-2 text-xs font-medium text-bad">
          <ShieldAlert size={14} /> A failed QC lot is blocking dispatch — re-inspect, or the owner can override with a reason on confirm.
        </div>
      ) : null}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Order" value={data.orderQty.toLocaleString("en-IN")} sub={`pcs · ${data.currency}`} />
        <Tile label="Cartons" value={data.packing ? String(data.packing.cartonsTotal) : "—"} sub={data.packing ? `${data.packing.pcsPerCarton}/carton` : "set on plan"} />
        <Tile label="HSN" value={data.hsn} sub={data.rosctlEligible ? `RoSCTL ~${(data.rosctlRateBps / 100).toFixed(2)}% (indicative)` : "—"} tone={data.rosctlEligible ? "ok" : undefined} />
        <Tile
          label="Status"
          value={data.dispatch ? (data.dispatch.status === "confirmed" ? "Shipped" : "Draft") : "Not started"}
          sub={data.dispatch?.invoiceId ? "invoice drafted" : ""}
          tone={data.dispatch?.status === "confirmed" ? "ok" : undefined}
        />
      </div>

      {data.dispatch === null ? (
        <PlanDispatch styleId={styleId} canDispatch={data.canDispatch} onDone={invalidate} />
      ) : data.dispatch.status === "draft" ? (
        <ConfirmDispatch data={data} dispatch={data.dispatch} onDone={invalidate} />
      ) : (
        <ShippedSummary dispatch={data.dispatch} onDone={invalidate} />
      )}
    </div>
  );
}

/** State 1 — no dispatch yet: plan packing + shipping docs. */
function PlanDispatch({ styleId, canDispatch, onDone }: { styleId: string; canDispatch: boolean; onDone: () => Promise<void> }) {
  const [dispatchDate, setDispatchDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pcsPerCarton, setPcsPerCarton] = useState("48");
  const [shippingBillNo, setShippingBillNo] = useState("");
  const [portOfLoading, setPortOfLoading] = useState("");
  const [destination, setDestination] = useState("");

  const create = trpc.dispatch.createDraft.useMutation({
    onSuccess: async () => { await onDone(); toast.success("Dispatch drafted"); },
    onError: (e: { message: string }) => toast.error("Couldn't draft dispatch", { description: e.message }),
  });

  if (!canDispatch) {
    return <p className="text-sm text-muted">This style isn't in production, so a new dispatch can't be started here.</p>;
  }

  return (
    <div className="rounded-md border border-line bg-bg p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Plan dispatch</h3>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Dispatch date">
          <input type="date" value={dispatchDate} onChange={(e) => { const v = e.currentTarget.value; setDispatchDate(v); }} className={inputCls} />
        </Field>
        <Field label="Pcs / carton"><Input value={pcsPerCarton} onChange={(e) => { const v = e.currentTarget.value; setPcsPerCarton(v); }} inputMode="numeric" className="h-8 w-20 text-xs" /></Field>
        <Field label="Shipping bill"><Input value={shippingBillNo} onChange={(e) => { const v = e.currentTarget.value; setShippingBillNo(v); }} placeholder="optional" className="h-8 w-28 text-xs" /></Field>
        <Field label="Port of loading"><Input value={portOfLoading} onChange={(e) => { const v = e.currentTarget.value; setPortOfLoading(v); }} placeholder="Chennai" className="h-8 w-28 text-xs" /></Field>
        <Field label="Destination"><Input value={destination} onChange={(e) => { const v = e.currentTarget.value; setDestination(v); }} placeholder="Felixstowe, UK" className="h-8 w-32 text-xs" /></Field>
        <Button size="sm" disabled={create.isPending} onClick={() => {
          const n = Math.round(Number(pcsPerCarton));
          if (!Number.isFinite(n) || n <= 0) { toast.error("Enter pieces per carton"); return; }
          create.mutate({ styleId, dispatchDate, pcsPerCarton: n, shippingBillNo: shippingBillNo.trim() || null, portOfLoading: portOfLoading.trim() || null, destination: destination.trim() || null });
        }}>
          <PackageCheck size={14} /> Build dispatch
        </Button>
      </div>
    </div>
  );
}

/** State 2 — draft dispatch: shipping docs + confirm (route, FX, override). */
function ConfirmDispatch({ data, dispatch, onDone }: { data: DispatchData; dispatch: NonNullable<DispatchData["dispatch"]>; onDone: () => Promise<void> }) {
  const isForeign = data.currency.toUpperCase() !== "INR";
  const [route, setRoute] = useState<Route>(isForeign ? "export_lut" : "taxable_cgst_sgst");
  const [fxRate, setFxRate] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  // Pre-flight validation surfaced on the button itself (not just a post-click
  // toast) so the operator sees *why* Confirm is blocked before clicking.
  const fxPaise = Math.round(Number(fxRate));
  const fxMissing = isForeign && (!Number.isFinite(fxPaise) || fxPaise <= 0);
  const overrideMissing = data.qcBlocked && !overrideReason.trim();
  const blockedReason = fxMissing
    ? `Enter the FX rate (paise per ${data.currency}) first`
    : overrideMissing
      ? "QC failed — enter an owner override reason first"
      : "";

  const confirm = trpc.dispatch.confirm.useMutation({
    onSuccess: async () => { await onDone(); toast.success(`Dispatched — ${invoiceLabel(route).toLowerCase()} drafted for review`); },
    onError: (e: { message: string }) => toast.error("Couldn't confirm dispatch", { description: e.message }),
  });

  return (
    <div className="flex flex-col gap-3">
      <PackingDocs dispatch={dispatch} />
      <div className="rounded-md border border-line bg-bg p-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Confirm &amp; draft {invoiceLabel(route).toLowerCase()}</h3>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Export route">
            <FormSelect
              ariaLabel="Export route"
              value={route}
              onValueChange={(v) => setRoute(v as Route)}
              options={isForeign
                ? [{ value: "export_lut", label: "Export under LUT (0%)" }, { value: "export_with_igst", label: "Export with IGST" }]
                : [{ value: "taxable_cgst_sgst", label: "Domestic (CGST/SGST)" }, { value: "taxable_igst", label: "Domestic (IGST)" }]}
              triggerClassName="h-8 w-52 px-2 text-xs"
            />
          </Field>
          {isForeign ? (
            <Field label={`FX ₹/${data.currency} (paise)`}>
              <Input value={fxRate} onChange={(e) => { const v = e.currentTarget.value; setFxRate(v); }} placeholder="8400" inputMode="numeric" className="h-8 w-24 text-xs" />
            </Field>
          ) : null}
          {data.qcBlocked ? (
            <Field label="QC override reason (owner)">
              <Input value={overrideReason} onChange={(e) => { const v = e.currentTarget.value; setOverrideReason(v); }} placeholder="Buyer waiver on file" className="h-8 w-48 text-xs" />
            </Field>
          ) : null}
          <Button size="sm" disabled={confirm.isPending || blockedReason !== ""} title={blockedReason || undefined} onClick={() => {
            if (blockedReason !== "") { toast.error(blockedReason); return; }
            confirm.mutate({
              dispatchId: dispatch.id,
              taxTreatment: route,
              fxRateToInrPaise: isForeign ? String(Math.round(Number(fxRate))) : undefined,
              overrideReason: data.qcBlocked ? overrideReason.trim() : null,
            });
          }}>
            <Ship size={14} /> Confirm dispatch
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted">Confirm freezes the dispatch, drafts the {invoiceLabel(route).toLowerCase()} for the CA to review, and moves the style to Shipped. {isForeign ? "FOB is invoiced in the style currency at the rate you enter." : null}</p>
      </div>
    </div>
  );
}

/** State 3 — confirmed: shipped summary + invoice link + post-ship doc editor. */
function ShippedSummary({ dispatch, onDone }: { dispatch: NonNullable<DispatchData["dispatch"]>; onDone: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [shippingBillNo, setShippingBillNo] = useState(dispatch.shippingBillNo ?? "");
  const [shippingBillDate, setShippingBillDate] = useState(dispatch.shippingBillDate ?? "");
  const [blAwbNo, setBlAwbNo] = useState(dispatch.blAwbNo ?? "");
  const [vesselOrFlight, setVesselOrFlight] = useState(dispatch.vesselOrFlight ?? "");
  const [portOfLoading, setPortOfLoading] = useState(dispatch.portOfLoading ?? "");
  const [destination, setDestination] = useState(dispatch.destination ?? "");
  const [cha, setCha] = useState(dispatch.cha ?? "");
  const [containerRef, setContainerRef] = useState(dispatch.containerRef ?? "");

  const update = trpc.dispatch.update.useMutation({
    onSuccess: async () => { setEditing(false); await onDone(); toast.success("Shipping documents updated"); },
    onError: (e: { message: string }) => toast.error("Couldn't update documents", { description: e.message }),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone="ok" size="sm" dot>Shipped</StatusPill>
        <span className="text-xs text-muted">Dispatched {formatDate(dispatch.dispatchDate)} · {dispatch.cartonsTotal} cartons · {dispatch.packedQty.toLocaleString("en-IN")} pcs</span>
        <div className="ml-auto flex items-center gap-3">
          <Button size="sm" intent="ghost" onClick={() => setEditing((v) => !v)}>
            <Pencil size={13} /> {editing ? "Cancel" : "Edit docs"}
          </Button>
          {dispatch.invoiceId ? (
            <Link to="/sales/invoices/$invoiceId" params={{ invoiceId: dispatch.invoiceId }} className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline">
              <FileText size={13} /> {invoiceLabel(dispatch.taxTreatment)} →
            </Link>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="rounded-md border border-line bg-bg p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Shipping documents</h3>
          <p className="mb-2 text-xs text-muted">Add or amend any shipping paperwork — the shipping bill is filed at the port before loading, BL / AWB and vessel details come in after the vessel sails.</p>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Shipping bill no."><Input value={shippingBillNo} onChange={(e) => { const v = e.currentTarget.value; setShippingBillNo(v); }} placeholder="SB-2026-04521" className="h-8 w-40 text-xs" /></Field>
            <Field label="Shipping bill date">
              <input type="date" value={shippingBillDate} onChange={(e) => setShippingBillDate(e.currentTarget.value)} className="h-8 rounded-sm border border-line bg-paper px-2 font-mono text-xs text-ink outline-none focus-visible:border-sky-600" />
            </Field>
            <Field label="BL / AWB"><Input value={blAwbNo} onChange={(e) => { const v = e.currentTarget.value; setBlAwbNo(v); }} placeholder="MEDUXX123456" className="h-8 w-40 text-xs" /></Field>
            <Field label="Vessel / flight"><Input value={vesselOrFlight} onChange={(e) => { const v = e.currentTarget.value; setVesselOrFlight(v); }} placeholder="MV Maersk Surabaya" className="h-8 w-44 text-xs" /></Field>
            <Field label="Port of loading"><Input value={portOfLoading} onChange={(e) => { const v = e.currentTarget.value; setPortOfLoading(v); }} placeholder="INMAA (Chennai)" className="h-8 w-40 text-xs" /></Field>
            <Field label="Destination"><Input value={destination} onChange={(e) => { const v = e.currentTarget.value; setDestination(v); }} placeholder="Hamburg, DE" className="h-8 w-40 text-xs" /></Field>
            <Field label="CHA"><Input value={cha} onChange={(e) => { const v = e.currentTarget.value; setCha(v); }} placeholder="AllCargo Logistics" className="h-8 w-44 text-xs" /></Field>
            <Field label="Container"><Input value={containerRef} onChange={(e) => { const v = e.currentTarget.value; setContainerRef(v); }} placeholder="LCL · 12.4 CBM" className="h-8 w-36 text-xs" /></Field>
            <Button size="sm" disabled={update.isPending} onClick={() => update.mutate({
              dispatchId: dispatch.id,
              shippingBillNo: shippingBillNo.trim() || null,
              shippingBillDate: shippingBillDate.trim() || null,
              blAwbNo: blAwbNo.trim() || null,
              vesselOrFlight: vesselOrFlight.trim() || null,
              portOfLoading: portOfLoading.trim() || null,
              destination: destination.trim() || null,
              cha: cha.trim() || null,
              containerRef: containerRef.trim() || null,
            })}>
              Save documents
            </Button>
          </div>
        </div>
      ) : null}

      <PackingDocs dispatch={dispatch} />
    </div>
  );
}

function PackingDocs({ dispatch }: { dispatch: NonNullable<DispatchData["dispatch"]> }) {
  const rows: Array<[string, string | null]> = [
    ["Shipping bill", dispatch.shippingBillNo],
    ["BL / AWB", dispatch.blAwbNo],
    ["Vessel / flight", dispatch.vesselOrFlight],
    ["Port of loading", dispatch.portOfLoading],
    ["Destination", dispatch.destination],
    ["CHA", dispatch.cha],
    ["Container", dispatch.containerRef],
  ];
  return (
    <div className="grid gap-x-6 gap-y-1.5 rounded-md border border-line bg-paper p-3 sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-3 text-xs">
          <span className="text-muted">{k}</span>
          <span className={v ? "text-ink" : "text-muted/60"}>{v ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}

const inputCls = "h-8 rounded-sm border border-line bg-paper px-2 text-xs text-ink outline-none focus-visible:border-sky-600";

function Tile({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "ok" | undefined }) {
  return (
    <div className="rounded-md border border-line bg-paper p-3">
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">{label}</div>
      <div className={`mt-1 font-mono text-xl font-bold ${tone === "ok" ? "text-emerald-600" : "text-ink"}`}>{value}</div>
      <div className="mt-1 text-xs text-muted">{sub}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">{label}</span>
      {children}
    </label>
  );
}
